import React, { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/supabaseClient';
import { useEffectiveMe } from '@/lib/effectiveMe';
import { hasCap } from '@/lib/capabilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { useSettingsHierarchy } from '@/lib/settingsHierarchy';
import SettingsHierarchy from '@/components/settings/SettingsHierarchy';
import SettingsLoadingState from '@/components/settings/SettingsLoadingState';
import SettingsErrorState from '@/components/settings/SettingsErrorState';
import SettingsConfirmDialog from '@/components/settings/SettingsConfirmDialog';
import {
  SettingsCompanyList,
  SettingsDepartmentList,
  SettingsLocationList,
} from '@/components/settings/SettingsScopedLists';

const EMPTY_FORM = { name: '', slug: '', code: '' };

function slugify(value) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function actionButtonProps(testId, label) {
  return {
    type: 'button',
    size: 'sm',
    variant: 'outline',
    className: 'shrink-0',
    'data-testid': testId,
    'aria-label': label,
  };
}

export default function SettingsOrganization({
  testId = 'settings-organization',
  mode = 'organization',
}) {
  const me = useEffectiveMe();
  const canCreateCompany = Boolean(me?.is_global && hasCap(me, 'company.update'));
  const canUpdateCompany = hasCap(me, 'company.update');
  const canDeleteCompany = Boolean(me?.is_global && hasCap(me, 'company.update'));
  const canCreateLocation = hasCap(me, 'location.create');
  const canUpdateLocation = hasCap(me, 'location.update');
  const canDeleteLocation = hasCap(me, 'location.delete');
  const canCreateDepartment = hasCap(me, 'department.create');
  const canUpdateDepartment = hasCap(me, 'department.update');
  const canDeleteDepartment = hasCap(me, 'department.delete');
  const showCompanyActions = mode === 'organization';
  const showLocationActions = mode === 'organization';
  const {
    companies,
    locationsByCompany,
    departmentsByLocation,
    loading,
    error,
    reload,
  } = useSettingsHierarchy({ eagerDepartments: mode === 'organization' || mode === 'departments' });

  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const openCreate = (type, parentId = '') => {
    setEditing(null);
    setCreating({ type, parentId });
    setForm(EMPTY_FORM);
  };

  const openEdit = (type, entity) => {
    setCreating(null);
    setEditing({
      type,
      id: entity.id,
      name: entity.name || '',
      code: entity.code || '',
    });
    setForm({
      name: entity.name || '',
      slug: entity.slug || '',
      code: entity.code || '',
    });
  };

  const closeEditor = () => {
    if (saving) return;
    resetEditor();
  };

  const resetEditor = () => {
    setCreating(null);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const editor = editing || creating;
  const entityType = editor?.type;
  const isCreating = Boolean(creating);
  const editorTitle = isCreating
    ? ({ company: 'Nuova azienda', location: 'Nuovo locale', department: 'Nuovo reparto' }[entityType])
    : ({ company: 'Modifica azienda', location: 'Modifica locale', department: 'Rinomina reparto' }[entityType]);
  const nameLabel = { company: 'Nome azienda', location: 'Nome locale', department: 'Nome reparto' }[entityType];
  const editorDialogTestId = mode === 'departments'
    ? (isCreating ? 'reparti-create-dialog' : 'reparti-rename-dialog')
    : `${testId}-editor-dialog`;
  const editorNameTestId = mode === 'departments'
    ? (isCreating ? 'reparti-create-name' : 'reparti-rename-name')
    : `${testId}-editor-name`;
  const editorSaveTestId = mode === 'departments'
    ? (isCreating ? 'reparti-create-save' : 'reparti-rename-save')
    : `${testId}-editor-save`;
  const originalEntity = editing ? {
    name: editing.name || '',
    code: editing.code || '',
  } : null;
  const isDirty = isCreating
    || form.name.trim() !== originalEntity?.name
    || (entityType === 'location' && form.code.trim() !== originalEntity?.code);

  const handleNameChange = (event) => {
    const name = event.target.value;
    setForm((current) => ({
      ...current,
      name,
      ...(isCreating && entityType === 'company' && !current.slug ? { slug: slugify(name) } : {}),
    }));
  };

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name || saving) return;
    if (isCreating && entityType === 'company' && !form.slug.trim()) return;
    setSaving(true);
    try {
      let path;
      let body;
      if (entityType === 'company') {
        path = isCreating ? '/api/companies' : `/api/companies/${editing.id}`;
        body = isCreating
          ? { name, slug: form.slug.trim() }
          : { name };
      } else if (entityType === 'location') {
        path = isCreating ? '/api/locations' : `/api/locations/${editing.id}`;
        body = isCreating
          ? { company_id: creating.parentId, name, code: form.code.trim() || null }
          : { name, code: form.code.trim() || null };
      } else {
        path = isCreating
          ? `/api/locations/${creating.parentId}/departments`
          : `/api/departments/${editing.id}`;
        body = { name };
      }
      await apiFetch(path, { method: isCreating ? 'POST' : 'PATCH', body: JSON.stringify(body) });
      toast.success(isCreating ? 'Elemento creato' : 'Modifica salvata');
      resetEditor();
      await reload();
    } catch (saveError) {
      toast.error(saveError.message || 'Impossibile salvare la modifica');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type, entity) => {
    const labels = { company: 'azienda', location: 'locale', department: 'reparto' };
    try {
      await apiFetch(
        type === 'company'
          ? `/api/companies/${entity.id}`
          : type === 'location'
            ? `/api/locations/${entity.id}`
            : `/api/departments/${entity.id}`,
        { method: 'DELETE' },
      );
      toast.success(`${labels[type][0].toUpperCase()}${labels[type].slice(1)} eliminat${type === 'company' ? 'a' : 'o'}`);
      await reload();
    } catch (deleteError) {
      toast.error(deleteError.message || `Impossibile eliminare ${labels[type]}`);
      throw deleteError;
    }
  };

  const renderCompany = ({ company }) => (
    <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
      {showCompanyActions && canCreateLocation && (
        <Button
          {...actionButtonProps(`settings-add-location-${company.id}`, `Aggiungi un locale a ${company.name}`)}
          onClick={() => openCreate('location', company.id)}
        >
          <Plus className="mr-1 h-4 w-4" aria-hidden="true" /> Locale
        </Button>
      )}
      {showCompanyActions && canUpdateCompany && (
        <Button
          {...actionButtonProps(`settings-edit-company-${company.id}`, `Modifica ${company.name}`)}
          onClick={() => openEdit('company', company)}
        >
          <Pencil className="mr-1 h-4 w-4" aria-hidden="true" /> Modifica
        </Button>
      )}
      {showCompanyActions && canDeleteCompany && (
        <Button
          {...actionButtonProps(`settings-delete-company-${company.id}`, `Elimina ${company.name}`)}
          className="text-red-600 hover:border-red-200 hover:bg-red-50 hover:text-red-700"
          onClick={() => setDeleteTarget({ type: 'company', entity: company })}
        >
          <Trash2 className="mr-1 h-4 w-4" aria-hidden="true" /> Elimina
        </Button>
      )}
    </div>
  );

  const renderLocation = ({ company, location }) => (
    <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
      {canCreateDepartment && (
        <Button
          {...actionButtonProps(
            mode === 'departments' ? `reparti-add-${location.id}` : `settings-add-department-${location.id}`,
            `Aggiungi un reparto a ${location.name}`,
          )}
          onClick={() => openCreate('department', location.id)}
        >
          <Plus className="mr-1 h-4 w-4" aria-hidden="true" /> {mode === 'departments' ? 'Aggiungi' : 'Reparto'}
        </Button>
      )}
      {showLocationActions && canUpdateLocation && (
        <Button
          {...actionButtonProps(`settings-edit-location-${location.id}`, `Modifica ${location.name}`)}
          onClick={() => openEdit('location', location)}
        >
          <Pencil className="mr-1 h-4 w-4" aria-hidden="true" /> Modifica
        </Button>
      )}
      {showLocationActions && canDeleteLocation && (
        <Button
          {...actionButtonProps(`settings-delete-location-${location.id}`, `Elimina ${location.name}`)}
          className="text-red-600 hover:border-red-200 hover:bg-red-50 hover:text-red-700"
          onClick={() => setDeleteTarget({ type: 'location', entity: location })}
        >
          <Trash2 className="mr-1 h-4 w-4" aria-hidden="true" /> Elimina
        </Button>
      )}
    </div>
  );

  const renderDepartment = ({ department }) => (
    <div className="flex shrink-0 items-center gap-1.5">
      {canUpdateDepartment && (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          data-testid={`${mode === 'departments' ? 'reparti-rename' : 'settings-edit-department'}-${department.id}`}
          onClick={() => openEdit('department', department)}
          aria-label={`Rinomina ${department.name}`}
          title="Rinomina"
        >
          <Pencil className="h-4 w-4" aria-hidden="true" />
        </Button>
      )}
      {canDeleteDepartment && (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="text-red-600 hover:bg-red-50 hover:text-red-700"
            data-testid={`${mode === 'departments' ? 'reparti-delete' : 'settings-delete-department'}-${department.id}`}
            onClick={() => setDeleteTarget({ type: 'department', entity: department })}
          aria-label={`Elimina ${department.name}`}
          title="Elimina"
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </Button>
      )}
    </div>
  );

  const renderScopedContent = () => {
    if (mode === 'company') {
      return (
        <SettingsCompanyList
          companies={companies}
          canCreate={canCreateCompany}
          canUpdate={canUpdateCompany}
          onCreate={() => openCreate('company')}
          onEdit={(company) => openEdit('company', company)}
          testId={`${testId}-companies`}
        />
      );
    }
    if (mode === 'locations') {
      return (
        <SettingsLocationList
          companies={companies}
          locationsByCompany={locationsByCompany}
          canCreate={canCreateLocation}
          canUpdate={canUpdateLocation}
          onCreate={(companyId) => openCreate('location', companyId)}
          onEdit={(location) => openEdit('location', location)}
          testId={`${testId}-locations`}
        />
      );
    }
    if (mode === 'departments') {
      return (
        <SettingsDepartmentList
          companies={companies}
          locationsByCompany={locationsByCompany}
          departmentsByLocation={departmentsByLocation}
          canCreate={canCreateDepartment}
          canUpdate={canUpdateDepartment}
          canDelete={canDeleteDepartment}
          onCreate={(locationId) => openCreate('department', locationId)}
          onEdit={(department) => openEdit('department', department)}
            onDelete={(department) => setDeleteTarget({ type: 'department', entity: department })}
          testId={testId}
        />
      );
    }
    return (
      <SettingsHierarchy
        companies={companies}
        locationsByCompany={locationsByCompany}
        departmentsByLocation={departmentsByLocation}
        testId={`${testId}-hierarchy`}
        renderCompany={showCompanyActions ? renderCompany : undefined}
        renderLocation={renderLocation}
        renderDepartment={renderDepartment}
      />
    );
  };

  return (
    <div className="space-y-4" data-testid={testId}>
      {showCompanyActions && canCreateCompany && (
        <div className="flex justify-end">
          <Button
            type="button"
            data-testid={`${testId}-add-company`}
            onClick={() => openCreate('company')}
            className="bg-brand-green text-white hover:bg-brand-green-hover"
          >
            <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" /> Aggiungi azienda
          </Button>
        </div>
      )}
      {loading && <SettingsLoadingState />}
      {!loading && error && <SettingsErrorState message={error} onRetry={reload} />}
      {!loading && !error && renderScopedContent()}

      <Dialog open={Boolean(editor)} onOpenChange={(open) => { if (!open) closeEditor(); }}>
        <DialogContent className="max-w-sm" data-testid={editorDialogTestId}>
          <DialogHeader>
            <DialogTitle>{editorTitle}</DialogTitle>
            <DialogDescription>
              {isCreating && entityType === 'location' && 'Il locale verrà aggiunto all’azienda selezionata.'}
              {isCreating && entityType === 'department' && 'Il reparto verrà aggiunto al locale selezionato.'}
              {!isCreating && 'Salva il nome aggiornato senza modificare il parent.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor={`${testId}-editor-name`}>{nameLabel}</Label>
              <Input
                id={`${testId}-editor-name`}
                autoFocus
                value={form.name}
                onChange={handleNameChange}
                disabled={saving}
                data-testid={editorNameTestId}
              />
            </div>
            {entityType === 'company' && isCreating && (
              <div className="space-y-1.5">
                <Label htmlFor={`${testId}-editor-slug`}>Slug</Label>
                <Input
                  id={`${testId}-editor-slug`}
                  value={form.slug}
                  onChange={(event) => setForm((current) => ({ ...current, slug: event.target.value }))}
                  disabled={saving}
                  placeholder="es. ristorante-centro"
                  data-testid={`${testId}-editor-slug`}
                />
              </div>
            )}
            {entityType === 'location' && (
              <div className="space-y-1.5">
                <Label htmlFor={`${testId}-editor-code`}>Codice locale (facoltativo)</Label>
                <Input
                  id={`${testId}-editor-code`}
                  value={form.code}
                  onChange={(event) => setForm((current) => ({ ...current, code: event.target.value }))}
                  disabled={saving}
                  data-testid={`${testId}-editor-code`}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={closeEditor} disabled={saving}>Annulla</Button>
            <Button
              type="button"
              data-testid={editorSaveTestId}
              onClick={handleSave}
              disabled={!form.name.trim() || !isDirty || (isCreating && entityType === 'company' && !form.slug.trim()) || saving}
              className="bg-brand-green text-white hover:bg-brand-green-hover"
            >
              {saving ? 'Attendi…' : (isCreating && entityType === 'department' ? 'Crea' : 'Salva')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SettingsConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Eliminare il reparto?"
        description={deleteTarget ? `Stai per eliminare ${deleteTarget.type === 'company' ? 'l’azienda' : deleteTarget.type === 'location' ? 'il locale' : 'il reparto'} “${deleteTarget.entity.name}”.` : ''}
        consequences="L’operazione richiede una conferma esplicita e viene bloccata dal server se esistono dipendenti, turni, ferie, presenze o altre dipendenze collegate."
        confirmLabel={`Elimina ${deleteTarget?.type === 'company' ? 'azienda' : deleteTarget?.type === 'location' ? 'locale' : 'reparto'}`}
        destructive
        onConfirm={async () => {
          await handleDelete(deleteTarget.type, deleteTarget.entity);
          setDeleteTarget(null);
        }}
      />
    </div>
  );
}